import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import { endpoints } from "../apiConfig";
import { colors, fonts, space, radius, card, pageHeading, buttonSecondary } from "../theme";
import HelpButton from "../components/HelpButton";
import QrSheetModal from "../components/QrSheet";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [businessName, setBusinessName] = useState("");
  const [version, setVersion] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [copiedLink, setCopiedLink] = useState("");
  const [showQr, setShowQr] = useState(false);
  const [dashboardEnabled, setDashboardEnabled] = useState(true);
  const [reportsEnabled, setReportsEnabled] = useState(true);
  const [savingToggles, setSavingToggles] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    axios.get(endpoints.dashboard, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setStats(res.data));

    axios.get(endpoints.health).then((res) => setVersion(res.data.version)).catch(() => {});

    const tenant = JSON.parse(localStorage.getItem("tenant") || "null");
    if (tenant) {
      setBusinessName(tenant.business_name);
      setTenantId(tenant.id);
      setDashboardEnabled(tenant.dashboard_enabled !== false);
      setReportsEnabled(tenant.reports_enabled !== false);
    }

    // Fetch feature toggles from server
    axios.get(endpoints.featureToggles, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setDashboardEnabled(res.data.dashboard_enabled);
        setReportsEnabled(res.data.reports_enabled);
      })
      .catch(() => {});
  }, []);

  const waiterLink = `${window.location.origin}/waiter-login?tenant=${tenantId}`;
  const kitchenLink = `${window.location.origin}/kitchen-login?tenant=${tenantId}`;
  const managerLink = `${window.location.origin}/manager-login?tenant=${tenantId}`;

  const copyLink = (link, label) => {
    navigator.clipboard.writeText(link);
    setCopiedLink(label);
    setTimeout(() => setCopiedLink(""), 2000);
  };

  const saveToggles = async () => {
    setSavingToggles(true);
    const token = localStorage.getItem("token");
    try {
      await axios.post(
        endpoints.featureToggles,
        { dashboard_enabled: dashboardEnabled, reports_enabled: reportsEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update localStorage
      const tenant = JSON.parse(localStorage.getItem("tenant") || "null");
      if (tenant) {
        tenant.dashboard_enabled = dashboardEnabled;
        tenant.reports_enabled = reportsEnabled;
        localStorage.setItem("tenant", JSON.stringify(tenant));
      }
    } catch (err) {
      console.error("Failed to save toggles:", err);
    } finally {
      setSavingToggles(false);
    }
  };

  return (
    <div style={{ display: "flex", background: colors.bgPage, minHeight: "100vh" }}>
      <Sidebar businessName={businessName} />
      <div className="app-content" style={{ flex: 1, padding: space[6] }}>
        <h1 style={pageHeading}>Dashboard <HelpButton page="dashboard" /></h1>
        
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: space[4], marginBottom: space[4] }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: colors.textPrimary, marginBottom: space[3] }}>Feature Controls</div>
          
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: space[3], marginBottom: space[3], borderBottom: `1px solid ${colors.border}` }}>
            <label style={{ fontSize: 14, color: colors.textPrimary }}>Dashboard</label>
            <button style={{ width: 50, height: 28, borderRadius: 14, border: "none", cursor: "pointer", background: dashboardEnabled ? colors.brandBlue : colors.surfaceMuted, position: "relative", padding: 0, display: "flex", alignItems: "center" }} onClick={() => setDashboardEnabled(!dashboardEnabled)}>
              <div style={{ position: "absolute", width: 24, height: 24, borderRadius: "50%", background: "#fff", marginLeft: dashboardEnabled ? 24 : 0, transition: "margin-left 0.2s" }} />
            </button>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: space[3], marginBottom: space[3], borderBottom: `1px solid ${colors.border}` }}>
            <label style={{ fontSize: 14, color: colors.textPrimary }}>Reports</label>
            <button style={{ width: 50, height: 28, borderRadius: 14, border: "none", cursor: "pointer", background: reportsEnabled ? colors.brandBlue : colors.surfaceMuted, position: "relative", padding: 0, display: "flex", alignItems: "center" }} onClick={() => setReportsEnabled(!reportsEnabled)}>
              <div style={{ position: "absolute", width: 24, height: 24, borderRadius: "50%", background: "#fff", marginLeft: reportsEnabled ? 24 : 0, transition: "margin-left 0.2s" }} />
            </button>
          </div>

          <button onClick={saveToggles} disabled={savingToggles} style={{ padding: "10px 20px", borderRadius: radius.md, border: "none", background: colors.brandBlue, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: savingToggles ? 0.6 : 1 }}>
            {savingToggles ? "Saving..." : "Save"}
          </button>
        </div>

        {stats ? (
          <div style={{ display: "flex", gap: space[4], marginTop: space[4], flexWrap: "wrap" }}>
            <StatCard label="Orders" value={stats.order_count} />
            <StatCard label="Staff" value={stats.staff_count} />
            <StatCard label="Tables" value={stats.table_count} />
            <StatCard label="Days remaining" value={stats.trial_days_left ?? "N/A"} />
          </div>
        ) : (
          <p style={{ fontFamily: fonts.body, color: colors.textSecondary }}>Loading…</p>
        )}

        {tenantId && (
          <div style={{ ...card, marginTop: space[5], maxWidth: 480 }}>
            <div style={{ fontFamily: fonts.display, fontWeight: 600, color: colors.textPrimary, marginBottom: 6 }}>
              Staff Login Links
            </div>
            <p style={{ fontFamily: fonts.body, fontSize: 13, color: colors.textSecondary, marginTop: 0 }}>
              Share these with your team so they can log in with their PIN (set under Staff).
              This confirms the PIN works but doesn't include a live ordering or
              kitchen-display screen. That's part of the full Alvna Order platform.
            </p>
            <LinkRow label="Waiter Login" link={waiterLink} onCopy={() => copyLink(waiterLink, "waiter")} copied={copiedLink === "waiter"} />
            <LinkRow label="Kitchen Login" link={kitchenLink} onCopy={() => copyLink(kitchenLink, "kitchen")} copied={copiedLink === "kitchen"} />
            <LinkRow label="Manager Login" link={managerLink} onCopy={() => copyLink(managerLink, "manager")} copied={copiedLink === "manager"} />
            <button onClick={() => setShowQr(true)} style={{ ...buttonSecondary, marginTop: space[3], width: "100%" }}>
              Print QR codes
            </button>
          </div>
        )}

        {showQr && (
          <QrSheetModal
            businessName={businessName}
            links={[
              { key: "waiter", label: "Waiter", url: waiterLink },
              { key: "kitchen", label: "Kitchen", url: kitchenLink },
              { key: "manager", label: "Manager", url: managerLink, private: true },
            ]}
            onClose={() => setShowQr(false)}
          />
        )}

        {version && (
          <div style={{ marginTop: 32, fontSize: 11, color: colors.textMuted, fontFamily: fonts.body }}>
            Alvna Remo v{version}
          </div>
        )}
      </div>
    </div>
  );
}

function LinkRow({ label, link, onCopy, copied }) {
  return (
    <div className="link-row" style={{ display: "flex", alignItems: "center", gap: space[2], marginTop: space[2], flexWrap: "wrap" }}>
      <span style={{ fontFamily: fonts.body, fontSize: 13, color: colors.textSecondary, minWidth: 90 }}>{label}:</span>
      <input
        readOnly
        value={link}
        style={{
          flex: "1 1 180px", minWidth: 0, fontSize: 12, fontFamily: fonts.body, padding: "6px 8px",
          borderRadius: radius.sm, border: `1px solid ${colors.border}`, color: colors.textPrimary,
        }}
        onFocus={(e) => e.target.select()}
      />
      <button
        onClick={onCopy}
        style={{
          padding: "6px 10px", fontSize: 12, borderRadius: radius.sm,
          border: `1px solid ${colors.border}`, background: colors.surfaceMuted, cursor: "pointer",
        }}
      >
        {copied ? "Copied!" : "Copy"}
      </button>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="stat-card" style={{ ...card, minWidth: 140 }}>
      <div style={{ fontFamily: fonts.body, fontSize: 12, color: colors.textSecondary }}>{label}</div>
      <div style={{ fontFamily: fonts.display, fontSize: 28, fontWeight: 700, color: colors.textPrimary }}>{value}</div>
    </div>
  );
}
